import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { Message, useToaster } from 'rsuite';
import { API_DOMAIN } from '../config';
import { uploadFormData } from '../api/request';

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Напишіть текст...',
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate({ editor }) {
      onChange(editor.isEmpty ? '' : editor.getHTML());
    },
  });
  const toaster = useToaster();
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!editor) {
      return;
    }
    const normalizedValue = value?.trim() ? value : '';
    const normalizedEditor = editor.isEmpty ? '' : editor.getHTML();
    if (normalizedValue !== normalizedEditor) {
      editor.commands.setContent(normalizedValue, false);
    }
  }, [editor, value]);

  const toggleLink = () => {
    if (!editor) {
      return;
    }
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Вкажіть URL', previousUrl);
    if (url === null) {
      return;
    }
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: trimmed, rel: 'noopener noreferrer' })
      .run();
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    toaster.push(
      <Message showIcon type={type} closable>
        {text}
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );
  };

  const handleFileClick = () => {
    if (!fileInputRef.current || uploading) {
      return;
    }
    fileInputRef.current.click();
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!editor) {
      showMessage('error', 'Редактор ще не готовий до вставки зображення.');
      return;
    }

    if (!file.type?.startsWith('image/')) {
      showMessage('error', 'Дозволені лише зображення.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const response = await uploadFormData(
        `${API_DOMAIN}/api/uploads`,
        formData
      );
      if (!response.ok) {
        showMessage('error', 'Не вдалося завантажити зображення.');
        return;
      }
      const data = await response.json().catch(() => null);
      const url = data?.url;
      if (!url) {
        showMessage('error', 'Сервер не повернув посилання на зображення.');
        return;
      }
      editor
        .chain()
        .focus()
        .setImage({
          src: url,
          alt: data?.originalName || file.name,
        })
        .run();
      showMessage('success', 'Зображення завантажено.');
    } catch (error) {
      showMessage('error', 'Не вдалося завантажити зображення.');
    } finally {
      setUploading(false);
    }
  };

  const toolbarButton = (
    label: string,
    onClick: () => void,
    isActive = false,
    disabled = false
  ) => (
    <button
      type="button"
      className={`rich-text-editor__button${
        isActive ? ' rich-text-editor__button--active' : ''
      }`}
      onClick={onClick}
      aria-pressed={isActive}
      disabled={disabled}
    >
      {label}
    </button>
  );

  return (
    <div
      className={`rich-text-editor${
        ariaInvalid ? ' rich-text-editor--invalid' : ''
      }`}
    >
      <div className="rich-text-editor__toolbar">
        {toolbarButton(
          'B',
          () => editor?.chain().focus().toggleBold().run(),
          !!editor?.isActive('bold'),
          !editor?.can().chain().focus().toggleBold().run()
        )}
        {toolbarButton(
          'I',
          () => editor?.chain().focus().toggleItalic().run(),
          !!editor?.isActive('italic'),
          !editor?.can().chain().focus().toggleItalic().run()
        )}
        {toolbarButton(
          'U',
          () => editor?.chain().focus().toggleUnderline().run(),
          !!editor?.isActive('underline'),
          !editor?.can().chain().focus().toggleUnderline().run()
        )}
        {toolbarButton(
          'H2',
          () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
          !!editor?.isActive('heading', { level: 2 })
        )}
        {toolbarButton(
          'H3',
          () => editor?.chain().focus().toggleHeading({ level: 3 }).run(),
          !!editor?.isActive('heading', { level: 3 })
        )}
        {toolbarButton(
          'UL',
          () => editor?.chain().focus().toggleBulletList().run(),
          !!editor?.isActive('bulletList')
        )}
        {toolbarButton(
          'OL',
          () => editor?.chain().focus().toggleOrderedList().run(),
          !!editor?.isActive('orderedList')
        )}
        {toolbarButton(
          'Quote',
          () => editor?.chain().focus().toggleBlockquote().run(),
          !!editor?.isActive('blockquote')
        )}
        {toolbarButton('Link', toggleLink, !!editor?.isActive('link'))}
        {toolbarButton(
          uploading ? 'Завантаження...' : 'Image',
          handleFileClick,
          false,
          uploading || !editor
        )}
        {toolbarButton('Clear', () =>
          editor?.chain().focus().clearNodes().unsetAllMarks().run()
        )}
      </div>
      <EditorContent
        editor={editor}
        id={id}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className="rich-text-editor__content"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        hidden
      />
    </div>
  );
}
